/**
 * widgetCatalog — the SINGLE source of truth for a widget type's presentation
 * (TV1-2291 · S18).
 *
 * Three surfaces used to answer "what is this widget called, and what is it for?"
 * independently, and all three answered badly:
 *
 * | Surface | Before |
 * |---|---|
 * | `PaletteDrawer` | the raw type string as BOTH label and description, behind a `// TODO: hacer description para cada widget` |
 * | `CollapsedBar` | the raw type string as the tooltip |
 * | `WidgetInspector` | its own private `WIDGET_TYPE_FALLBACK` table + its own `dataSourceOf` |
 *
 * They now all read from here. Adding a widget type is a DATA EDIT in this file:
 * `WIDGET_CATALOG` is a `Record<WidgetType, …>`, so a new member of the `WidgetType`
 * union does not compile until it has a source, a label and a description. That is
 * deliberate — a type that reaches the palette without them regresses straight back
 * to rendering `donutChart` at the user.
 *
 * ## Grouping is by DATA SOURCE, not by chart shape
 *
 * The palette's previous classifier was `w.includes( 'Chart' ) ? 'Charts' : 'All
 * Widgets'` over a four-member `Category` union, so `Favorites` and `Tables` were
 * **structurally always empty** — two dead tabs shipped for months. Grouping is
 * derived from real data here instead, and {@link groupWidgetTypesBySource} DROPS
 * empty groups, so a dead group cannot be rendered even in principle.
 *
 * "What is it reading" is also the more useful question than "what shape is it":
 * a `kpi` and a `barChart` differ in shape but are configured identically, whereas
 * a `text` block shares a shape with nothing and is configured by typing prose.
 *
 * ## All three sources have members (t25 — the `FORTHCOMING` split is RETIRED)
 *
 * Ticket 18 minted the `analytics | readmodel | static` source union with no
 * `readmodel` member, because the four readmodel-backed types (`count`, `alert`,
 * `list`, `progress` — SDD §6) had no renderers. Ticket 26 parked their entries in
 * a second table, `FORTHCOMING_WIDGET_CATALOG`, so that a surface which had to
 * DESCRIBE one (the inspector, handed a persisted `alert`) could, while the palette
 * — which reads `ALL_WIDGET_TYPES`, derived from `WIDGET_CATALOG`'s keys — could
 * not OFFER a card `WebWidget` was unable to draw.
 *
 * Ticket 25 lands those renderers and widens the front-end `WidgetType` union, so
 * the holding pen has done its job: **the four entries moved VERBATIM into
 * `WIDGET_CATALOG` and the second table is gone.** No consumer changed — every one
 * of them already went through {@link getWidgetCatalogEntry} / {@link widgetDataSourceOf},
 * which is exactly what the split was designed to make possible.
 *
 * `heading` / `spacer` (static) went the other way in ticket 21 — renderer, union
 * membership and catalog entry in ONE change — so they were full members from the
 * start, and the compiler is what demanded them.
 *
 * ## i18n
 *
 * The catalog stores the SPANISH fallback (the primary UI language) beside the
 * type; the i18n key is derived by {@link widgetLabelKey} /
 * {@link widgetDescriptionKey} and the real strings live in
 * `src/translation/{es,en}/Widgets.json`. Consumers call
 * `t( widgetLabelKey( type ), widgetLabelFallback( type ))`.
 *
 * The keys keep the `widget_inspector_` prefix ticket 16 minted them under, even
 * though the catalog now serves the floating palette too: renaming ten shipped keys
 * to gain a prefix buys nothing and would silently blank the label for anyone on a
 * cached bundle.
 */

import type { WidgetType } from './Widgets/types';

// =============================================================================
// Data sources
// =============================================================================

/**
 * Where a widget's data comes from.
 *
 * - `analytics` — a Cube query (`cube` + `measures`, the `WidgetBuilder` sections).
 * - `readmodel` — a PV3 readmodel query (see `Widgets/readmodelSource.ts`). Four
 *   members since ticket 25; `READMODEL_WIDGET_TYPES` is the same fact keyed by type.
 * - `static` — no query at all: the user types the content (`DATALESS_WIDGET_TYPES`).
 */
export type WidgetDataSource = 'analytics' | 'readmodel' | 'static';

/** Display order for the palette's groups. */
export const WIDGET_DATA_SOURCE_ORDER: readonly WidgetDataSource[] = [
  'analytics',
  'readmodel',
  'static'
];

/** Spanish fallbacks for the group headings. Keys via {@link widgetSourceLabelKey}. */
export const WIDGET_DATA_SOURCE_LABEL: Record<WidgetDataSource, string> = {
  analytics: 'Analítica (Cube)',
  readmodel: 'Datos operativos',
  static: 'Contenido propio'
};

// =============================================================================
// The catalog
// =============================================================================

/**
 * What a widget is FOR — the axis the palette groups by (2026-08-18).
 *
 * ⚠️ Deliberately NOT {@link WidgetDataSource}. That axis answers "where does this
 * read from", which is the right question for the CONFIGURATION pane (it decides
 * which fields you get) and the wrong one for the palette, where the user is
 * asking "what do I want this card to LOOK like". The two genuinely disagree:
 * `kpi` is Cube-backed and `count` is readmodel-backed, but both are one big
 * number in a tile and belong side by side when you are choosing.
 *
 * Grouping also bounds the bar. A flat strip grows by one icon per widget type
 * forever; five named groups grow by one ENTRY inside an existing group, and the
 * bar stays the same width however many types exist.
 */
export type WidgetGroup = 'numbers' | 'charts' | 'tables' | 'status' | 'content';

/** Display order for the palette's groups — most-reached-for first. */
export const WIDGET_GROUP_ORDER: readonly WidgetGroup[] = [
  'numbers', 'charts', 'tables', 'status', 'content'
];

/** Spanish fallbacks for the group names. Keys via {@link widgetGroupLabelKey}. */
export const WIDGET_GROUP_LABEL: Record<WidgetGroup, string> = {
  numbers: 'Números',
  charts: 'Gráficos',
  tables: 'Tablas y listas',
  status: 'Estado',
  content: 'Contenido'
};

export const widgetGroupLabelKey = ( group: WidgetGroup ): string =>
  `widget_group_${ group }`;

export const widgetGroupLabelFallback = ( group: WidgetGroup ): string =>
  WIDGET_GROUP_LABEL[group];

export type WidgetCatalogEntry = {
  /**
   * The renderable `WidgetType` this entry describes.
   *
   * It was `WidgetType | ForthcomingWidgetType` between tickets 26 and 25, while the
   * four readmodel types were catalogued but undrawable. They are drawable now, so
   * the union is back to one member — see the header.
   */
  type: WidgetType;
  source: WidgetDataSource;
  /** What the card is FOR — the palette's grouping axis. @see WidgetGroup */
  group: WidgetGroup;
  /** Human name. Spanish fallback for `widget_inspector_type_<type>`. */
  label: string;
  /**
   * ONE line saying what the card does — what the palette shows under the label,
   * and what replaces the `// TODO: hacer description para cada widget`. Written
   * for a seller, not for a developer: it describes the READING, not the
   * implementation.
   */
  description: string;
  /**
   * Still RENDERABLE, no longer OFFERED (2026-08-18).
   *
   * A deprecated type is filtered out of {@link ALL_WIDGET_TYPES} — which is what
   * every palette reads — but keeps its catalog entry, so a persisted widget of
   * that type still resolves a label, a description and a data source everywhere
   * that has to describe one (the inspector, the card header, a test fixture).
   *
   * This is the ONLY safe way to retire a type: the schema union is the command
   * gate and the event schema, so dropping a member would stop existing layouts
   * from validating.
   */
  deprecated?: boolean;
};

/**
 * Every type the front-end `WidgetType` union contains — exhaustively, by
 * construction. Order is the palette's within-group order.
 */
export const WIDGET_CATALOG: Record<WidgetType, WidgetCatalogEntry> = {
  areaChart: {
    type: 'areaChart',
    source: 'analytics',
    group: 'charts',
    label: 'Área',
    description: 'Evolución en el tiempo, con el área bajo la línea rellena.'
  },
  barChart: {
    type: 'barChart',
    source: 'analytics',
    group: 'charts',
    label: 'Barras',
    description: 'Compara valores entre categorías, una barra por categoría.'
  },
  lineChart: {
    type: 'lineChart',
    source: 'analytics',
    group: 'charts',
    label: 'Líneas',
    description: 'Tendencia de una o varias métricas a lo largo del tiempo.'
  },
  pieChart: {
    type: 'pieChart',
    source: 'analytics',
    group: 'charts',
    label: 'Torta',
    description: 'Cómo se reparte un total entre pocas categorías.'
  },
  donutChart: {
    type: 'donutChart',
    source: 'analytics',
    group: 'charts',
    label: 'Dona',
    description: 'Igual que la torta, con el total en el centro.'
  },
  scatterChart: {
    type: 'scatterChart',
    source: 'analytics',
    group: 'charts',
    label: 'Dispersión',
    description: 'Relación entre dos métricas, un punto por registro.'
  },
  criticalStock: {
    type: 'criticalStock',
    source: 'analytics',
    group: 'tables',
    label: 'Stock crítico',
    description: 'Ítems con menos días de stock, los más urgentes primero.'
  },
  table: {
    type: 'table',
    source: 'analytics',
    group: 'tables',
    label: 'Tabla',
    description: 'Filas y columnas, con totales y pivoteo opcional.'
  },
  kpi: {
    type: 'kpi',
    source: 'analytics',
    group: 'numbers',
    label: 'KPI',
    description: 'Un número grande, con la variación contra el período anterior.'
  },
  text: {
    type: 'text',
    source: 'static',
    group: 'content',
    label: 'Nota',
    description: 'Texto propio en markdown; no consulta ningún dato.'
  },
  // TV1-2291 (t21) — the two static structure widgets. `source: 'static'` is not a
  // label choice: it is what puts them in the palette's "Contenido propio" group
  // instead of alongside the Cube-backed cards, and it is the same bit
  // `DATALESS_WIDGET_TYPES` carries for the fetch path.
  heading: {
    type: 'heading',
    source: 'static',
    group: 'content',
    label: 'Título de sección',
    description: 'Separa el informe en secciones, con un título y una línea.'
  },
  spacer: {
    type: 'spacer',
    source: 'static',
    group: 'content',
    label: 'Espaciador',
    description: 'Un hueco a propósito: ocupa lugar y no dibuja nada.',
    /*
     * RETIRED FROM THE PALETTE (2026-08-18), NOT FROM THE SCHEMA.
     *
     * ⚠️ Deleting the entry outright is a NARROWING, and narrowing this union is
     * how you brick a saved report: `UiWidgetSchema` is the command gate AND the
     * event schema, so a layout that already contains a spacer would stop
     * validating — it could not be re-saved, and the readmodel could not replay it.
     * Every widening in this file was careful about that; a removal has to be at
     * least as careful.
     *
     * So the type stays renderable and describable, and only stops being OFFERED.
     * A report that has one keeps it and keeps working; nobody can add another.
     */
    deprecated: true
  },
  // TV1-2291 (t25) — the four READMODEL-sourced types, moved VERBATIM out of the
  // retired `FORTHCOMING_WIDGET_CATALOG` (ticket 26's holding pen) now that they
  // have renderers. The copy is unchanged on purpose: it is already translated in
  // both `es` and `en` under the keys `widgetLabelKey` / `widgetDescriptionKey`
  // derive, so rewording it here would silently blank a shipped string.
  //
  // `source: 'readmodel'` is not a label choice — it is what puts them in the
  // palette's "Datos operativos" group, what makes `WidgetInspector` mount the
  // readmodel pane instead of the seven Cube sections, and the same bit
  // `READMODEL_WIDGET_TYPES` carries for the FETCH path.
  count: {
    type: 'count',
    source: 'readmodel',
    group: 'numbers',
    label: 'Conteo',
    description: 'Cuántas filas cumplen una condición, en un número grande.'
  },
  alert: {
    type: 'alert',
    source: 'readmodel',
    group: 'status',
    label: 'Alerta',
    description: 'Un conteo con color de urgencia y un enlace para ir a resolverlo.'
  },
  list: {
    type: 'list',
    source: 'readmodel',
    group: 'tables',
    label: 'Listado',
    description: 'Las últimas filas de una colección, en vez de un total.'
  },
  progress: {
    type: 'progress',
    source: 'readmodel',
    group: 'status',
    label: 'Progreso',
    description: 'Barra de avance de un proceso por lote.'
  }
};

/**
 * Every widget type the renderer can draw, in catalog order.
 *
 * This is what a host passes as `availableWidgets` — `WidgetLayoutComponent`
 * narrows it per device via `getDeviceWidgetTypes`. Hoisted here so the persisted
 * report, the generated PREVIEW and the palette cannot drift apart: a type one of
 * them omitted would render as `undefined` there and correctly elsewhere, which is
 * exactly the kind of "works in prod, not in preview" difference nobody thinks to
 * look for.
 *
 * A module-level constant on purpose — consumers pass it by reference into props
 * whose effects key on identity.
 */
export const ALL_WIDGET_TYPES: WidgetType[] = ( Object.keys( WIDGET_CATALOG ) as WidgetType[])
  // Deprecated types are renderable but not offerable — see `deprecated` on the
  // entry type. This is the one list every palette reads, so filtering here is
  // what makes "retired" true everywhere at once.
  .filter(( type ) => !WIDGET_CATALOG[type].deprecated );

// =============================================================================
// Lookups
// =============================================================================

/**
 * The catalog entry for a type, or `undefined`.
 *
 * Takes `string` rather than `WidgetType` on purpose: a PERSISTED layout can carry
 * a type this build does not know (an older/newer schema), and every caller below
 * degrades to something readable instead of throwing inside a render.
 *
 * ⚠️ Between tickets 26 and 25 this consulted TWO tables, the renderable one and a
 * `FORTHCOMING_WIDGET_CATALOG` holding pen, so that a surface which had to DESCRIBE
 * a widget it was handed answered correctly for a type the palette could not yet
 * OFFER. Ticket 25 merged the pen into `WIDGET_CATALOG`, so there is one table
 * again — and every consumer survived the merge untouched precisely because they
 * all went through this function. Keep it that way: read the catalog HERE, never by
 * indexing `WIDGET_CATALOG` directly with a string of unknown provenance.
 *
 * `hasOwnProperty` rather than `in`, so `toString` and friends are not mistaken for
 * entries.
 */
export const getWidgetCatalogEntry = ( type: string ): WidgetCatalogEntry | undefined =>
  Object.prototype.hasOwnProperty.call( WIDGET_CATALOG, type )
    ? WIDGET_CATALOG[type as WidgetType]
    : undefined;

/**
 * Which group a type belongs to. An unknown type falls back to `analytics` — the
 * source the plurality of known types have — so it is still OFFERED rather than
 * silently vanishing from the palette.
 */
export const widgetDataSourceOf = ( type: string ): WidgetDataSource =>
  getWidgetCatalogEntry( type )?.source ?? 'analytics';

/** Spanish label fallback; the raw type for an unknown one (visible, not blank). */
export const widgetLabelFallback = ( type: string ): string =>
  getWidgetCatalogEntry( type )?.label ?? type;

/** Spanish description fallback; empty for an unknown type — never the type string. */
export const widgetDescriptionFallback = ( type: string ): string =>
  getWidgetCatalogEntry( type )?.description ?? '';

/** Spanish group-heading fallback. */
export const widgetSourceLabelFallback = ( source: WidgetDataSource ): string =>
  WIDGET_DATA_SOURCE_LABEL[source];

// =============================================================================
// i18n keys
// =============================================================================

export const widgetLabelKey = ( type: string ): string => `widget_inspector_type_${ type }`;

export const widgetDescriptionKey = ( type: string ): string => `widget_inspector_description_${ type }`;

export const widgetSourceLabelKey = ( source: WidgetDataSource ): string =>
  `widget_inspector_source_${ source }`;

// =============================================================================
// Grouping
// =============================================================================

export type WidgetSourceGroup = {
  source: WidgetDataSource;
  types: WidgetType[];
};

/**
 * Group the types a host offers, in {@link WIDGET_DATA_SOURCE_ORDER}, **dropping
 * empty groups**.
 *
 * The drop is the point: it is what makes a permanently-empty tab impossible. The
 * old palette hard-coded its four categories and rendered them whether or not
 * anything classified into them.
 */
export const groupWidgetTypesBySource = ( types: readonly WidgetType[]): WidgetSourceGroup[] =>
  WIDGET_DATA_SOURCE_ORDER
    .map(( source ) => ({
      source,
      types: types.filter(( type ) => widgetDataSourceOf( type ) === source )
    }))
    .filter(( group ) => group.types.length > 0 );

export type WidgetFunctionGroup = {
  group: WidgetGroup;
  types: WidgetType[];
};

/**
 * Group the types a host offers BY FUNCTION, in {@link WIDGET_GROUP_ORDER},
 * **dropping empty groups** — same guarantee as {@link groupWidgetTypesBySource},
 * for the same reason: a permanently-empty palette tab shipped for months once
 * (see the header), and the only durable fix is that an empty group cannot be
 * rendered even in principle.
 */
export const groupWidgetTypesByFunction = (
  types: readonly WidgetType[]
): WidgetFunctionGroup[] =>
  WIDGET_GROUP_ORDER
    .map(( group ) => ({
      group,
      types: types.filter(( type ) => getWidgetCatalogEntry( type )?.group === group )
    }))
    .filter(( entry ) => entry.types.length > 0 );
